import React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  TRIAGE_PROGRESS_STATES,
  TRIAGE_STATE_META,
  triageStateIndex,
} from "../../lib/triageStates";

/**
 * Terminal-style processing HUD for live triage state machine updates.
 */
export default function TriageTerminalHUD({ status, error, isLoading }) {
  if (error && !status) {
    return (
      <div className="border border-red-500/50 bg-slate-950 font-mono text-sm">
        <div className="border-b border-red-500/30 bg-red-950/40 px-4 py-2 text-red-400">
          AUDIT_ENGINE :: CONNECTION_ERROR
        </div>
        <div className="px-4 py-4 text-red-300">{error.message}</div>
      </div>
    );
  }

  if (isLoading && !status) {
    return (
      <div className="border border-slate-700 bg-slate-950 font-mono text-sm">
        <div className="border-b border-slate-700 bg-slate-900 px-4 py-2 text-slate-400 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-cyan-400" />
          AUDIT_ENGINE :: INITIALIZING
        </div>
        <div className="px-4 py-6 text-slate-500">Initiating corporate asset review...</div>
      </div>
    );
  }

  if (!status) return null;

  const isFailed = status.state === "FAILED";
  const isFiled = status.state === "FILED";
  const isAuditComplete = status.state === "AWAITING_ACCOUNT";
  const currentIndex = triageStateIndex(status.state);

  return (
    <div className="border border-slate-700 bg-slate-950 font-mono text-sm">
      <div className="border-b border-slate-700 bg-slate-900 px-4 py-2 flex items-center justify-between">
        <span className="text-cyan-400">AUDIT_ENGINE :: JOB_{status.jobId.slice(0, 8)}</span>
        <span
          className={isFailed ? "text-red-400" : isFiled ? "text-emerald-400" : "text-amber-400"}
        >
          {status.state}
        </span>
      </div>

      <div className="px-4 py-4 space-y-1">
        {isFailed ? (
          <div className="flex items-start gap-2 text-red-300">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <p>
                &gt; HALT :: {status.failureStage ?? "UNKNOWN"} -{" "}
                {status.failureReason ?? "No reason recorded."}
              </p>
            </div>
          </div>
        ) : (
          TRIAGE_PROGRESS_STATES.map((state, index) => {
            const meta = TRIAGE_STATE_META[state];
            const isDone =
              index < currentIndex || (index === currentIndex && (isFiled || isAuditComplete));
            const isCurrent = index === currentIndex && !isFiled && !isAuditComplete;
            const prefix = isDone ? "[OK]" : isCurrent ? "[>>]" : "[..]";

            return (
              <div
                key={state}
                className={
                  isDone ? "text-emerald-400/90" : isCurrent ? "text-cyan-300" : "text-slate-600"
                }
              >
                <span className="text-slate-500 mr-2">{prefix}</span>
                <span className="text-slate-500 mr-2">{state}</span>
                {(isDone || isCurrent) && (
                  <span className="text-slate-300">→ {meta.terminalLine}</span>
                )}
                {isCurrent && (
                  <Loader2 className="inline h-3.5 w-3.5 ml-2 animate-spin text-cyan-400" />
                )}
              </div>
            );
          })
        )}
      </div>

      {status.updatedAt && (
        <div className="border-t border-slate-800 px-4 py-2 text-xs text-slate-600">
          last_sync: {new Date(status.updatedAt).toLocaleString()}
        </div>
      )}
    </div>
  );
}
